import { DataTable } from "../../components/layout/DataTable";
import { EmptyState } from "../../components/layout/EmptyState";
import { MetricCard } from "../../components/layout/MetricCard";
import { Panel } from "../../components/layout/Panel";
import { WarningPanel } from "../../components/layout/WarningPanel";
import { SectionIntro } from "../../components/layout/SectionIntro";
import { useSystemStatusSummary } from "../../app/useSystemStatusSummary";
import { formatDateTime } from "../../lib/format";

export function SystemWorkspace() {
  const { errors, integrationStatus, normalizedSystemStatus, staleStreams, systemStatus } =
    useSystemStatusSummary();
  const heartbeatWarning =
    systemStatus?.worker_healthy === false
      ? systemStatus.worker_heartbeat_seconds != null
        ? `Background job heartbeat stale. Last heartbeat: ${systemStatus.worker_heartbeat_seconds} seconds ago.`
        : "Background job heartbeat missing."
      : null;
  const systemSummary = (() => {
    const summary: string[] = [];
    summary.push(`System status is ${normalizedSystemStatus || 'unknown'}.`);
    if (systemStatus?.worker_heartbeat_seconds != null) {
      summary.push(`Background heartbeat age is ${systemStatus.worker_heartbeat_seconds} seconds.`);
    }
    if (staleStreams.length > 0) {
      summary.push(`${staleStreams.length} stream(s) currently need freshness attention.`);
    } else {
      summary.push('No sync streams are currently marked stale.');
    }
    if ((integrationStatus?.streams || []).length > 0) {
      summary.push(`${integrationStatus?.streams.length || 0} sync stream snapshot(s) are available for review.`);
    }
    return summary;
  })();

  return (
    <>
      <SectionIntro
        eyebrow="System"
        title="Is everything connected and healthy?"
        description="See whether the app is ready to trust, whether data is current, and whether anything needs a quick settings or sync check."
      />
      <Panel className="status-strip">
        <MetricCard label="App Health" value={normalizedSystemStatus || "-"} />
        <MetricCard label="Background Sync Age" value={systemStatus?.worker_heartbeat_seconds ?? "-"} />
        <MetricCard label="Feeds Needing Attention" value={staleStreams.length} />
      </Panel>

      <WarningPanel
        title="Things to review"
        warnings={[
          ...errors,
          ...(heartbeatWarning ? [heartbeatWarning] : []),
          ...staleStreams.map(
            (stream) =>
              `${stream.stream} stale. Last success: ${formatDateTime(stream.last_success_at)}${
                stream.last_error ? ` | last error: ${stream.last_error}` : ""
              }`,
          ),
        ]}
      />

      <Panel>
        <h2>What to know right now</h2>
        <ul className="summary-list">
          {systemSummary.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </Panel>

      <Panel>
        <h2>Connected data feeds</h2>
        <p className="muted-small">These feeds keep planning, buying, receiving, and cash views up to date.</p>
        <DataTable>
          <table>
            <thead>
              <tr>
                <th>Stream</th>
                <th>Last Updated</th>
                <th>Age (sec)</th>
                <th>Stale</th>
                <th>Latest Status</th>
              </tr>
            </thead>
            <tbody>
              {(integrationStatus?.streams || []).map((stream) => (
                <tr key={stream.stream}>
                  <td>{stream.stream}</td>
                  <td>{formatDateTime(stream.last_success_at)}</td>
                  <td>{stream.freshness_seconds ?? "-"}</td>
                  <td>{stream.is_stale ? "Yes" : "No"}</td>
                  <td>{stream.last_run_status || "-"}</td>
                </tr>
              ))}
              {(integrationStatus?.streams || []).length === 0 && (
                <tr>
                  <td colSpan={5}>No sync stream snapshots are available yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </DataTable>
        {!integrationStatus && errors.length === 0 && (
          <EmptyState
            title="No system data yet"
            message="System and integration status snapshots will appear here after the first successful health refresh."
            compact
          />
        )}
      </Panel>
    </>
  );
}
